if (!customElements.get('c-volume-tiers')) {
  class CVolumeTiers extends HTMLElement {
    constructor() {
      super();
      this._tiers = [];
      this._selected = null;
      this._price = 0;
      this._compare = 0;
      this._timer = null;
      this._ready = false;
      this._pushing = false;
      this._onVariant = this._onVariant.bind(this);
      this._onInput = this._onInput.bind(this);
      this._onSync = this._onSync.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onKey = this._onKey.bind(this);
    }

    connectedCallback() {
      this._list = this.querySelector('[data-c-vt-list]');
      if (!this._list) return;
      try {
        this._config = JSON.parse(this.querySelector('[data-c-vt-config]').textContent);
      } catch (e) {
        this._config = { tiers: [] };
      }
      this._price = Number(this.dataset.price) || 0;
      this._compare = Number(this.dataset.compare) || 0;
      this._moneyFormat = this.dataset.moneyFormat || '{{amount}}';
      this._ready = false;
      this._showSkeleton();

      document.addEventListener('variant:changed', this._onVariant);
      window.addEventListener('syncProductQuantity', this._onSync);
      this._list.addEventListener('click', this._onClick);
      this._list.addEventListener('keydown', this._onKey);

      if (this._config.source === 'foxkit') {
        this._waitForFoxKit(Date.now());
      } else {
        this._init(this._themeTiers());
      }
    }

    disconnectedCallback() {
      document.removeEventListener('variant:changed', this._onVariant);
      window.removeEventListener('syncProductQuantity', this._onSync);
      if (this._list) {
        this._list.removeEventListener('click', this._onClick);
        this._list.removeEventListener('keydown', this._onKey);
      }
      if (this._input) {
        this._input.removeEventListener('input', this._onInput);
        this._input.removeEventListener('change', this._onInput);
      }
      clearTimeout(this._timer);
    }

    _showSkeleton() {
      this._list.innerHTML = '<div class="c-vt__skeleton"></div><div class="c-vt__skeleton"></div><div class="c-vt__skeleton"></div>';
    }

    _waitForFoxKit(start) {
      const tiers = this._foxKitTiers();
      if (tiers) {
        this._init(tiers);
        return;
      }
      if (Date.now() - start > 3000) {
        this._init(this._themeTiers());
        return;
      }
      this._timer = setTimeout(() => this._waitForFoxKit(start), 150);
    }

    _foxKitTiers() {
      const fk = window.FoxKitPlugins && window.FoxKitPlugins.QuantityDiscount;
      if (!Array.isArray(fk)) return null;
      const pid = String(this.dataset.productId);
      const entry = fk.find((d) => {
        if (!d || d.active === false || d.deleted) return false;
        if (d.applies_to === 'all_products') return true;
        return Array.isArray(d.target_products) && d.target_products.some((p) => {
          const id = p && typeof p === 'object' ? (p.id !== undefined ? p.id : p.product_id) : p;
          return String(id).indexOf(pid) !== -1;
        });
      });
      if (!entry || !Array.isArray(entry.discounts) || !entry.discounts.length) return null;
      const tiers = [];
      if (this._config.showSingle !== false) {
        tiers.push({ qty: 1, percent: 0, fixed: 0, label: '', highlighted: false });
      }
      entry.discounts
        .slice()
        .sort((a, b) => a.min_qty - b.min_qty)
        .forEach((d) => {
          const qty = parseInt(d.min_qty, 10);
          if (!qty || qty < 1) return;
          if (tiers.some((t) => t.qty === qty)) return;
          const isPct = String(d.type).toUpperCase() === 'PERCENTAGE';
          tiers.push({
            qty: qty,
            percent: isPct ? Number(d.value) || 0 : 0,
            fixed: isPct ? 0 : Math.round((Number(d.value) || 0) * 100),
            label: '',
            highlighted: !!d.highlighted
          });
        });
      return tiers;
    }

    _themeTiers() {
      return (this._config.tiers || []).map((t) => ({
        qty: t.qty,
        percent: t.percent || 0,
        fixed: 0,
        label: t.label || '',
        highlighted: false,
        id: t.id
      })).sort((a, b) => a.qty - b.qty);
    }

    _init(tiers) {
      if (!tiers.length) {
        this.hidden = true;
        return;
      }
      const hl = this._config.highlightTier;
      const fromTheme = this._config.source !== 'foxkit' || tiers.some((t) => t.id);
      tiers.forEach((t, i) => {
        if (hl === 'none') t.highlighted = false;
        else if (hl === 'foxkit') t.highlighted = fromTheme ? false : t.highlighted;
        else t.highlighted = !!t.id && String(t.id) === String(hl);
        t.index = i;
      });
      this._tiers = tiers;
      this._input = this._findInput();
      this.setAttribute('data-count', String(tiers.length));
      this._render();
      this.removeAttribute('data-state');

      if (this._config.defaultTier === 'highlighted') {
        const h = this._tiers.find((t) => t.highlighted) || this._tiers[0];
        this._select(h, true);
      } else {
        const q = this._input ? parseInt(this._input.value, 10) : 1;
        this._select(this._tierForQty(q) || this._tiers[0], false);
      }
      if (this._input) {
        this._input.addEventListener('input', this._onInput);
        this._input.addEventListener('change', this._onInput);
      }
      this._ready = true;
    }

    _findInput() {
      const sid = this.dataset.sectionId;
      let el = document.querySelector('input[name="quantity"][form="product-form-' + sid + '"]');
      if (!el) {
        const root = this.closest('.shopify-section') || document;
        el = root.querySelector('input[name="quantity"]');
      }
      return el;
    }

    _tierForQty(qty) {
      let found = null;
      this._tiers.forEach((t) => {
        if (t.qty <= qty && (!found || t.qty > found.qty)) found = t;
      });
      return found;
    }

    _money(cents) {
      const fmt = (n, precision, thousands, decimal) => {
        const fixed = (n / 100).toFixed(precision);
        const parts = fixed.split('.');
        const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        return parts[1] ? whole + decimal + parts[1] : whole;
      };
      const value = Math.round(Number(cents) || 0);
      return this._moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (m, name) => {
        switch (name) {
          case 'amount_no_decimals': return fmt(value, 0, ',', '.');
          case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
          case 'amount_with_apostrophe_separator': return fmt(value, 2, '\'', '.');
          case 'amount_no_decimals_with_space_separator': return fmt(value, 0, ' ', '.');
          case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
          default: return fmt(value, 2, ',', '.');
        }
      });
    }

    _unitPrice(t) {
      if (t.percent) return Math.round(this._price * (1 - t.percent / 100));
      if (t.fixed) return Math.max(0, this._price - t.fixed);
      return this._price;
    }

    _discountText(t) {
      if (t.percent) return (Math.round(t.percent * 100) / 100) + '%';
      if (t.fixed) return this._money(t.fixed);
      return '';
    }

    _fill(template, t) {
      return String(template || '')
        .replace(/\{\s*quantity\s*\}/g, t.qty)
        .replace(/\{\s*discount\s*\}/g, this._discountText(t))
        .replace(/\{\s*percent\s*\}/g, t.percent ? String(t.percent) : '');
    }

    _title(t) {
      if (t.label) return t.label;
      return this._fill(this._config.titleTemplate, t);
    }

    _render() {
      const c = this._config;
      const frag = document.createDocumentFragment();
      this._tiers.forEach((t) => {
        const hasDiscount = t.percent > 0 || t.fixed > 0;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'c-vt__tier';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', 'false');
        btn.tabIndex = -1;
        btn.dataset.index = String(t.index);

        const radio = document.createElement('span');
        radio.className = 'c-vt__radio';
        radio.setAttribute('aria-hidden', 'true');
        btn.appendChild(radio);

        const main = document.createElement('span');
        main.className = 'c-vt__main';
        const title = document.createElement('span');
        title.className = 'c-vt__title';
        title.textContent = this._title(t);
        main.appendChild(title);
        if (t.highlighted && c.highlightLabel) {
          const rb = document.createElement('span');
          rb.className = 'c-vt__ribbon';
          btn.classList.add('c-vt__tier--hl');
          rb.textContent = c.highlightLabel;
          main.appendChild(rb);
        }
        if (hasDiscount && c.badgeTemplate) {
          const badge = document.createElement('span');
          badge.className = 'c-vt__badge';
          badge.textContent = this._fill(c.badgeTemplate, t);
          main.appendChild(badge);
        }
        if (c.showUnit) {
          const unit = document.createElement('span');
          unit.className = 'c-vt__unit';
          unit.dataset.role = 'unit';
          main.appendChild(unit);
        }
        btn.appendChild(main);

        if (c.showTotal || (c.showCompare && hasDiscount) || (c.showSavings && hasDiscount)) {
          const prices = document.createElement('span');
          prices.className = 'c-vt__prices';
          if (c.showTotal) {
            const total = document.createElement('span');
            total.className = 'c-vt__total';
            total.dataset.role = 'total';
            prices.appendChild(total);
          }
          if (c.showCompare && hasDiscount) {
            const cmp = document.createElement('span');
            cmp.className = 'c-vt__compare';
            cmp.dataset.role = 'compare';
            prices.appendChild(cmp);
          }
          if (c.showSavings && hasDiscount) {
            const save = document.createElement('span');
            save.className = 'c-vt__save';
            save.dataset.role = 'save';
            prices.appendChild(save);
          }
          btn.appendChild(prices);
        }
        t.el = btn;
        frag.appendChild(btn);
      });
      this._list.innerHTML = '';
      this._list.appendChild(frag);

      if (!this._summary) {
        this._summary = document.createElement('p');
        this._summary.className = 'c-vt__summary';
        this._summary.setAttribute('aria-live', 'polite');
        this._list.parentNode.appendChild(this._summary);
      }
      this._updatePrices();
    }

    _updatePrices() {
      const c = this._config;
      this._tiers.forEach((t) => {
        if (!t.el) return;
        const unit = this._unitPrice(t);
        const total = unit * t.qty;
        const full = this._price * t.qty;
        const set = (role, text) => {
          const el = t.el.querySelector('[data-role="' + role + '"]');
          if (el) el.textContent = text;
        };
        if (c.showUnit) set('unit', this._money(unit) + ' each');
        if (c.showTotal) set('total', this._money(total));
        set('compare', this._money(full));
        set('save', 'Save ' + this._money(full - total));
        t.el.setAttribute('aria-label', this._ariaLabel(t, total));
      });
      this._updateSummary();
    }

    _ariaLabel(t, total) {
      let s = this._title(t);
      if (t.percent || t.fixed) s += ', ' + this._fill(this._config.badgeTemplate, t);
      if (this._config.showTotal) s += ', ' + this._money(total);
      return s;
    }

    _updateSummary() {
      if (!this._summary) return;
      const t = this._selected;
      if (!t) {
        this._summary.textContent = '';
        return;
      }
      const unit = this._unitPrice(t);
      const total = unit * t.qty;
      const full = this._price * t.qty;
      let s = t.qty + ' x ' + this._money(unit);
      if (this._config.showTotal) s += ' = ' + this._money(total);
      if (this._config.showSavings && full > total) s += ' (save ' + this._money(full - total) + ')';
      this._summary.textContent = s;
    }

    _select(t, push) {
      this._selected = t;
      this._tiers.forEach((x) => {
        const on = x === t;
        x.el.setAttribute('aria-checked', on ? 'true' : 'false');
        x.el.tabIndex = on ? 0 : -1;
      });
      this._updateSummary();
      if (push && t) this._setQuantity(t.qty);
    }

    _setQuantity(qty) {
      const input = this._input || (this._input = this._findInput());
      if (!input) return;
      let q = qty;
      const max = parseInt(input.getAttribute('max'), 10);
      const min = parseInt(input.getAttribute('min'), 10);
      if (max && q > max) q = max;
      if (min && q < min) q = min;
      this._pushing = true;
      input.value = String(q);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this._pushing = false;
    }

    _onClick(e) {
      const btn = e.target.closest('.c-vt__tier');
      if (!btn) return;
      const t = this._tiers[parseInt(btn.dataset.index, 10)];
      if (t) this._select(t, true);
    }

    _onKey(e) {
      const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (keys.indexOf(e.key) === -1 || !this._tiers.length) return;
      e.preventDefault();
      const cur = this._selected ? this._selected.index : 0;
      const n = this._tiers.length;
      let next = cur;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (cur + 1) % n;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (cur - 1 + n) % n;
      else if (e.key === 'Home') next = 0;
      else next = n - 1;
      const t = this._tiers[next];
      this._select(t, true);
      t.el.focus();
    }

    _onInput() {
      if (this._pushing || !this._input || !this._ready) return;
      const q = parseInt(this._input.value, 10);
      if (!q) return;
      this._select(this._tierForQty(q) || this._tiers[0], false);
    }

    _onSync(e) {
      if (this._pushing || !this._ready) return;
      const q = parseInt(e.detail, 10);
      if (q) this._select(this._tierForQty(q) || this._tiers[0], false);
    }

    _onVariant(e) {
      const v = e && e.detail && e.detail.variant;
      if (!v) return;
      this._price = Number(v.price) || 0;
      this._compare = Number(v.compare_at_price) || 0;
      if (this._ready) this._updatePrices();
    }
  }
  customElements.define('c-volume-tiers', CVolumeTiers);
}
